/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import TaskIcon from '@/components/TaskIcon';
import TaskTitle from '@/components/TaskTitle/TaskTitle';
import { useAppSelector } from '@/hooks';
import { createNumericKeyObject, isJSONObject } from '@/utils/utils';
import { FlexBox, Label, Option, Select, Text } from '@ui5/webcomponents-react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslations } from '../../hooks/useTranslations';
import * as storageUtils from '../../utils/storageUtils';
import { actions } from './../../store/Products';

type ControlTypeOptions = {
    translationValueKey: string;
    value: string;
    id: string;
};
interface SuccessMeasuresProps {
    content: {
        id: string;
        translationTitleKey: string;
        title: string;
        personalizationConfiguration: {
            configurationValue: string;
        };
        productConfigurationKey: string;
        controlType: {
            controlTypeOptions: ControlTypeOptions[];
        };
    };
}

const SuccessMeasures: React.FC<SuccessMeasuresProps> = ({ content }: SuccessMeasuresProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslations();
    const productList = useAppSelector(state => state.productList);
    const options = content.controlType?.controlTypeOptions?.map((option: ControlTypeOptions) => option);

    const [selected, setSelected] = useState<Record<string, string>>(() => {
        const savedStringified = content.personalizationConfiguration?.configurationValue;
        if (savedStringified && isJSONObject(savedStringified)) return JSON.parse(savedStringified);

        return createNumericKeyObject(3);
    });

    useEffect(() => {
        const savedStringified = content.personalizationConfiguration?.configurationValue;
        if (savedStringified && isJSONObject(savedStringified)) {
            setSelected(JSON.parse(savedStringified));
        } else {
            setSelected(createNumericKeyObject(3));
        }
    }, [content.personalizationConfiguration?.configurationValue]);

    const handleChange = (event: any, dropdown: string) => {
        setSelected((prevState: any) => ({
            ...prevState,
            [dropdown]: event.detail.selectedOption.dataset.value,
        }));
    };

    const getAvailableOptions = (dropdown: string): ControlTypeOptions[] => {
        const selectedValues = Object.keys(selected)
            .filter(key => key !== dropdown)
            .map(key => selected[key]);
        return options.filter((option: ControlTypeOptions) => !selectedValues.includes(option.translationValueKey));
    };

    useEffect(() => {
        updateStateVariable();
    }, [selected]);

    function updateStateVariable() {
        const USER_ID = storageUtils.getItem('user').user_uuid;
        dispatch(
            actions.setCurrentProductConfigurationValue({
                productRunId: productList.currentRunId,
                taskId: content.id,
                configurationKey: content.productConfigurationKey || 'NULL_SuccessMeasures',
                configurationValue: JSON.stringify(selected),
                createdBy: USER_ID,
                updatedBy: USER_ID,
                personalizationConfiguration: content.personalizationConfiguration,
            })
        );
        dispatch(
            actions.setCurrentProductConfigKeys({
                key: content.productConfigurationKey || 'NULL_SuccessMeasures',
                value: JSON.stringify(selected),
            })
        );
    }

    return (
        <div className="content__datacontroller" data-testid="success-measures">
            {
                <div
                    style={{
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <TaskIcon icon="target-group" />
                    <TaskTitle />
                    <Text data-testid="success-measures-question" style={{ marginBottom: '1rem' }}>
                        {t({ key: content.translationTitleKey, defaultText: content.title })}
                    </Text>

                    <FlexBox gap={4} direction="Column" style={{ width: '100%' }}>
                        {Object.entries(selected).map(([key, value]) => {
                            const options = getAvailableOptions(key);

                            return (
                                <FlexBox key={key} style={{ width: '100%' }} alignItems="Center" gap={16}>
                                    <Label>{key}</Label>
                                    <Select
                                        style={{ width: '100%' }}
                                        onLiveChange={e => handleChange(e, key)}
                                        value={value}
                                        data-testid={`select-${key}`}
                                    >
                                        <Option value="" data-value="">
                                            {t('SELECT')}
                                        </Option>
                                        {options.map(option => (
                                            <Option
                                                key={option.translationValueKey}
                                                value={option.translationValueKey}
                                                data-value={option.translationValueKey}
                                                selected={option.translationValueKey === value}
                                            >
                                                {t({ key: option.translationValueKey, defaultText: option.value })}
                                            </Option>
                                        ))}
                                    </Select>
                                </FlexBox>
                            );
                        })}
                    </FlexBox>
                </div>
            }
        </div>
    );
};

export default SuccessMeasures;
